import React, { useMemo } from 'react';

import { cn } from '@lyearn/style';
import { Box, BoxProps } from '@lyearn/ui';
import useDnd from '@/components/DragDrop/DropOverContainer/useDnd';

import { CategoryType } from '../../../types';
import { DraggingItemType } from '../../CategoryList/types';

export interface DropOverContainerProps extends BoxProps {
  disableTop?: boolean;
  disableMiddle?: boolean;
  disableBottom?: boolean;

  onDropTop?: (event: React.DragEvent<HTMLDivElement>) => void;
  onDropMiddle?: (event: React.DragEvent<HTMLDivElement>) => void;
  onDropBottom?: (event: React.DragEvent<HTMLDivElement>) => void;

  draggingItem: React.MutableRefObject<DraggingItemType | undefined>;
  hierarchyPath: string[];

  currentCategory: CategoryType;
}

const CategoryDropOver: React.FC<React.PropsWithChildren<DropOverContainerProps>> = (props) => {
  const {
    className,
    children,

    disableTop,
    disableMiddle,
    disableBottom,

    onDropMiddle: onDropMiddleProps,
    onDropTop: onDropTopProps,
    onDropBottom: onDropBottomProps,

    draggingItem,
    hierarchyPath,

    currentCategory,
  } = props;

  const hierarchyPathSet = useMemo(() => new Set(hierarchyPath), [hierarchyPath]);

  const dndTop = useDnd({
    onDropCallback: onDropTopProps,
    canDrop: () => {
      return (
        !hierarchyPathSet.has(draggingItem.current?.category.id ?? '') &&
        draggingItem.current?.category.id !== currentCategory.id
      );
    },
  });
  const dndMiddle = useDnd({
    onDropCallback: onDropMiddleProps,
    canDrop: () => {
      return (
        !hierarchyPathSet.has(draggingItem.current?.category.id ?? '') &&
        draggingItem.current?.category.id !== currentCategory.id
      );
    },
  });
  const dndBottom = useDnd({
    onDropCallback: onDropBottomProps,
    canDrop: () => {
      return (
        !hierarchyPathSet.has(draggingItem.current?.category.id ?? '') &&
        draggingItem.current?.category.id !== currentCategory.id
      );
    },
  });

  return (
    <Box className={cn('relative', className)}>
      <Box
        className={cn('absolute top-0 bottom-0 end-0 start-0', {
          'border border-interactive-primary': dndMiddle.isDragOver,
        })}
        style={{ transition: 'transform .2s' }}>
        <Box
          className={cn('h-[25%] w-full', {
            'border-t border-interactive-primary': dndTop.isDragOver,
          })}
          style={{ transition: 'transform .2s' }}
          {...(disableTop
            ? undefined
            : {
                onDragLeave: dndTop.onDragLeave,
                onDragOver: dndTop.onDragOver,
                onDrop: dndTop.onDrop,
              })}
        />
        <Box
          className="h-[50%] w-full"
          {...(disableMiddle
            ? undefined
            : {
                onDragLeave: dndMiddle.onDragLeave,
                onDragOver: dndMiddle.onDragOver,
                onDrop: dndMiddle.onDrop,
              })}
        />
        <Box
          className={cn('h-[25%] w-full', {
            'border-b border-interactive-primary': dndBottom.isDragOver,
          })}
          style={{ transition: 'transform .2s' }}
          {...(disableBottom
            ? undefined
            : {
                onDragLeave: dndBottom.onDragLeave,
                onDragOver: dndBottom.onDragOver,
                onDrop: dndBottom.onDrop,
              })}
        />
      </Box>
      {children}
    </Box>
  );
};

export default CategoryDropOver;
